import { Map } from 'immutable';
import moment from 'moment';
import { DEFAULT_TIME_ZONE } from 'utils/time-zone-util';
import { TimeUtil } from 'utils/time-util';
import {isIos, isMobile, isSwitch, PlatformUtil} from 'utils/platform';
import { DataDetailUtil } from 'components/exception/issue/DataDetail';
import { Base64 } from 'utils/base64';

/**
 * @param {Map} searchParams
 * @returns {*}
 */
export function makeCrashListSearchParamsFromSearchParams(searchParams) {
  let params = searchParams.filter((value) => value !== '' && value !== 'all');
  if (params.get('version')) {
    params = params.set('version', decodeURIComponent(params.get('version')));
  }
  if (params.get('bundleId')) {
    params = params.set('bundleId', decodeURIComponent(params.get('bundleId')));
  }
  if (params.get('channelId')) {
    params = params.set('channelId', decodeURIComponent(params.get('channelId')));
  }
  return params;
}

export const CrashUtil = (function() {
  function momentFormatWithDefault(input, outputFormat, fallback) {
    if (!input) {
      return fallback;
    }
    const m = moment.tz(input, DEFAULT_TIME_ZONE); // 将后台默认时区的数据转成项目本地时区的
    if (!m.isValid()) {
      return `${fallback} (${DEFAULT_TIME_ZONE})`;
    }
    const millis = m.valueOf();
    return moment(millis).format(outputFormat);
  }

  return Object.freeze({
    /** 检查上报是否是先锋云游戏 */
    checkIsGameMatrix(appId, crashMap) {
      const lowerModel = (crashMap.modelOriginalName || '').toLowerCase();
      const lowerRom = (crashMap.rom || '').toLowerCase();
      if (lowerModel === 'gamematrix') {
        return true;
      }
      // 国内DNF云游戏判定规则
      if (appId === '1104294055' && lowerModel.includes('noh-an00') && !lowerRom.startsWith('huawei')) {
        return true;
      }
      return false;
    },

    /** 加工getCrashDoc接口里的crashMap */
    makeRefinedCrashMap(crashMap) {
      if (!crashMap) {
        return {};
      }
      const crashTimeFormatted = Number(crashMap.crashTimestamp) > 0
        ? moment(TimeUtil.fixChaosMillis(crashMap.crashTimestamp)).format('YYYY-MM-DD HH:mm:ss')
        : momentFormatWithDefault(crashMap.crashTime, 'YYYY-MM-DD HH:mm:ss', crashMap.crashTime);

      const uploadTimeFormatted = Number(crashMap.uploadTimestamp) > 0
        ? moment(TimeUtil.fixChaosMillis(crashMap.uploadTimestamp)).format('YYYY-MM-DD HH:mm:ss')
        : momentFormatWithDefault(crashMap.uploadTime, 'YYYY-MM-DD HH:mm:ss', crashMap.uploadTime);

      const { addCodeFrame, retraceCrashDetail } = crashMap;
      const crashStackLines = (retraceCrashDetail || '').split('\n');
      let firstDefinitiveStackIndex = Array.isArray(addCodeFrame) && addCodeFrame.length > 0
        ? Number((addCodeFrame[0] || '').split(',')[0]) || 0
        : 0;
      const firstDefinitiveStackLine = crashStackLines[firstDefinitiveStackIndex] || '';

      return {
        ...crashMap,
        crashTimeFormatted,
        uploadTimeFormatted,
        firstDefinitiveStackLine,
      }
    },

    formatDeviceName(originalModel, productName) {
      if (!originalModel || !productName) {
        return originalModel || productName || '';
      }
      if (originalModel.toLowerCase() !== productName.toLowerCase()) {
        return `${productName} (${originalModel})`;
      }
      return originalModel;
    },

    /** 获取上报相关的符号UUID列表 */
    getSymbolUuidList({ pid, crashDoc, attachList, pcModuleInfoList, attachAllKvList, enableUsym }) {
      const { crashMap, esMap } = (crashDoc || {});
      if (!crashMap || !esMap) {
        return [];
      }
      let uuids = [];
      if ((isMobile(pid) || isSwitch(pid)) && crashMap.appUUID) {
        uuids = (crashMap.appUUID || '').split(',').filter(x => x);
      }
      const {
        appSoNameList,
      } = esMap || {};
      if (PlatformUtil.isIosOrMac(pid)) {
        // iOS额外增加UnityFramework模块。Mac额外增加appSoNameList中的模块。
        const soInfoLines = DataDetailUtil.makeSoInfoTextLines(pid, attachList);
        const extraModuleNames = isIos(pid)
          ? ['UnityFramework']
          : appSoNameList || [];
        const extraModuleLines = soInfoLines.filter(x => extraModuleNames.some(name => x.includes(name)));
        extraModuleLines.forEach((extraLine) => {
          // 格式是： UnityFramework: arm64;e9a1f943db113cccab834b045eb91aae;0x0000000115d88000 这样
          const extraUuid = (extraLine.split(';')[1] || '').toUpperCase();
          uuids.push(extraUuid);
        })
      } else if ((pcModuleInfoList || []).length > 0) {
        const minidumpUuids = (pcModuleInfoList || []).map(x => x.uuid).filter(x => x);
        uuids.push(...minidumpUuids);
      }

      // 增加unity符号 (usym)
      // --story=126887091 【CS】【p0】usym符号表状态提示
      if (enableUsym && crashMap.reservedMap?.il2cppUUID) {
        const usymUuid = 'USym' + crashMap.reservedMap?.il2cppUUID;
        uuids.push(usymUuid);
      } else if (enableUsym && PlatformUtil.isIosOrMac(pid) && attachAllKvList) {
        const appSoLibInfosBase64 = attachAllKvList.find(x => x.key === 'CrashSightAppSoLibInfos')?.value;
        if (appSoLibInfosBase64) {
          const appSoLibInfoStr = Base64.decodeAsString(appSoLibInfosBase64);
          const unitySymbolUuids = appSoLibInfoStr.split('|').filter(x => x).map(x => {
            const [binaryImageName, binaryArch, uuid, loadAddress] = (x || '').split(';');
            return binaryImageName === 'UnityFramework'
              ? 'USym' + uuid
              : null;
          }).filter(x => x);
          uuids.push(...unitySymbolUuids);
        }
      }

      return uuids;
    },
  });
})();
